import { useState } from 'react'
import { SkeletonList } from '../componentes/Skeleton'
import { EstadoError } from '../componentes/EstadoError'
import { EstadoVacio } from '../componentes/EstadoVacio'
import { EnlaceWhatsApp } from '../componentes/EnlaceWhatsApp'
import { IconoActualizar } from '../componentes/iconos'
import {
  useGrandesEmpresas,
  useFijarRecurrencia,
  type FilaGE,
  type OrdenGE,
} from '../hooks/useGrandesEmpresas'

/**
 * Sección Grandes Empresas (B.4 · B.10).
 *
 * Lista los clientes con etiqueta GE en tarjetas (opción B): nombre, CIF,
 * población, último reparto y recurrencia (7/14/28 días) con pills de un clic.
 * Los GE NO reciben WhatsApp: su gestión es manual desde aquí. El "+ Pedido"
 * (catálogo sin bonos) queda como fase 2.
 */

const OPCIONES_RECURRENCIA = [7, 14, 28] as const

/** Chip de la recurrencia actual del GE. */
function ChipRecurrencia({ dias }: { dias: number | null }) {
  if (dias === null) {
    return <span className="shrink-0 rounded-full border border-borde px-2.5 py-0.5 text-xs font-semibold text-tinta-apagada">sin fijar</span>
  }
  const cls =
    dias === 7
      ? 'text-positivo-tinta bg-positivo-fondo border-positivo-anillo'
      : dias === 14
        ? 'text-atencion-tinta bg-atencion-fondo border-atencion-anillo'
        : 'text-acento-tinta bg-acento-suave border-acento-borde'
  return <span className={['shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-semibold', cls].join(' ')}>cada {dias} días</span>
}

/** Fecha ISO (YYYY-MM-DD) → "20 sep 2026". */
function fechaCorta(iso: string | null): string {
  if (!iso) return '—'
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })
}

function TarjetaGE({
  ge,
  onFijar,
  ocupado,
}: {
  ge: FilaGE
  onFijar: (dias: number | null) => void
  ocupado: boolean
}) {
  return (
    <li className="flex flex-col gap-3 rounded-xl border border-borde bg-superficie p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-tinta">{ge.nombre}</p>
          <p className="text-xs text-tinta-apagada">CIF {ge.cif || '—'}</p>
        </div>
        <ChipRecurrencia dias={ge.recurrenciaDias} />
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
        <div>
          <span className="block text-tinta-apagada">Población</span>
          <span className="text-tinta-2">{ge.poblacion || '—'}</span>
        </div>
        <div>
          <span className="block text-tinta-apagada">Último reparto</span>
          <span className="text-tinta-2">{fechaCorta(ge.ultimoReparto)}</span>
        </div>
        {ge.telefono ? (
          <div>
            <span className="block text-tinta-apagada">Teléfono</span>
            <EnlaceWhatsApp
              telefono={ge.telefono}
              etiqueta={ge.telefono}
              className="text-acento-tinta underline underline-offset-2 hover:text-acento-fuerte"
            />
          </div>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs text-tinta-apagada">Recurrencia:</span>
        {OPCIONES_RECURRENCIA.map((d) => {
          const activa = ge.recurrenciaDias === d
          return (
            <button
              key={d}
              type="button"
              disabled={ocupado}
              onClick={() => onFijar(activa ? null : d)}
              aria-pressed={activa}
              className={[
                'rounded-full border px-3 py-1 text-xs font-semibold transition disabled:opacity-50',
                activa ? 'border-acento bg-acento text-tinta-inversa' : 'border-borde-fuerte text-tinta-2 hover:bg-elevado',
              ].join(' ')}
            >
              {d}d
            </button>
          )
        })}
      </div>
    </li>
  )
}

export function GrandesEmpresas() {
  const [orden, setOrden] = useState<OrdenGE>('ultimo_reparto')
  const ge = useGrandesEmpresas(orden)
  const fijar = useFijarRecurrencia()
  const items = ge.data?.items ?? []

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-tinta">Grandes Empresas</h1>
          <p className="mt-1 text-sm text-tinta-apagada">
            {ge.data ? `${ge.data.total} clientes GE. ` : ''}
            No reciben WhatsApp: se gestionan a mano desde aquí. Fija su recurrencia para
            preposicionarlos en el calendario.
          </p>
        </div>
        <button
          type="button"
          aria-label="Actualizar"
          onClick={() => void ge.refetch()}
          className="flex items-center gap-2 rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado"
        >
          <IconoActualizar className="size-4" />
          <span className="hidden sm:inline">Actualizar</span>
        </button>
      </div>

      {/* Orden */}
      <div className="flex gap-2">
        {([
          ['ultimo_reparto', 'Por último reparto'],
          ['poblacion', 'Por población'],
        ] as [OrdenGE, string][]).map(([clave, txt]) => (
          <button
            key={clave}
            type="button"
            onClick={() => setOrden(clave)}
            aria-pressed={orden === clave}
            className={[
              'rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
              orden === clave ? 'bg-acento text-tinta-inversa' : 'border border-borde-fuerte text-tinta-2 hover:bg-elevado',
            ].join(' ')}
          >
            {txt}
          </button>
        ))}
      </div>

      {ge.isPending ? (
        <SkeletonList />
      ) : ge.isError ? (
        <EstadoError error={ge.error} onReintentar={() => void ge.refetch()} />
      ) : items.length === 0 ? (
        <EstadoVacio
          titulo="Sin clientes GE"
          descripcion="No hay clientes con la etiqueta de Gran Empresa."
        />
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {items.map((g) => (
            <TarjetaGE
              key={g.contactId}
              ge={g}
              ocupado={fijar.isPending && fijar.variables?.contactId === g.contactId}
              onFijar={(dias) => fijar.mutate({ contactId: g.contactId, dias })}
            />
          ))}
        </ul>
      )}

      {fijar.isError ? (
        <p role="alert" className="text-sm text-critico-tinta">
          No se ha podido guardar la recurrencia. Inténtalo de nuevo.
        </p>
      ) : null}
    </div>
  )
}
